import React, { ReactNode } from 'react'
import Icon from '../Icon2/Icon'

export const ASSET_SCAN_STATUSES = {
  QUARANTINED: 'quarantined',
  CLEAN: 'clean',
  PENDING: 'pending',
  NOT_SCANNED: 'not_scanned',
};

export const TOOLTIP_CONTENT = {
  [ASSET_SCAN_STATUSES.QUARANTINED]:
    'Asset scan failed. Malware was detected in this file. Replace or delete this asset with a safe version to continue using it.',
  [ASSET_SCAN_STATUSES.CLEAN]: 'Asset scan successful. This asset is safe and ready to use.',
  [ASSET_SCAN_STATUSES.PENDING]:
    'Asset scan is in progress. This asset cannot be downloaded or used until the scan is complete.',
};

const SHIELD_CROSS_SVG =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.49 2.23006L5.49997 4.11006C4.34997 4.54006 3.40997 5.90006 3.40997 7.12006V14.5501C3.40997 15.7301 4.18997 17.2801 5.13997 17.9901L9.43997 21.2001C10.85 22.2601 13.17 22.2601 14.58 21.2001L18.88 17.9901C19.83 17.2801 20.61 15.7301 20.61 14.5501V7.12006C20.61 5.89006 19.67 4.53006 18.52 4.10006L13.53 2.23006C12.68 1.92006 11.32 1.92006 10.49 2.23006Z" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M14.15 13.4399L9.90002 9.18994" stroke="#292D32" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/><path d="M14.1 9.23999L9.84998 13.49" stroke="#292D32" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const SHIELD_SEARCH_SVG =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.59 10.5501V7.12006C20.59 5.89006 19.65 4.53006 18.5 4.10006L13.51 2.23006C12.68 1.92006 11.32 1.92006 10.49 2.23006L5.49997 4.11006C4.34997 4.54006 3.40997 5.90006 3.40997 7.12006V14.5501C3.40997 15.7301 4.18997 17.2801 5.13997 17.9901L9.43997 21.2001C10.14 21.7401 11.07 22.0001 12 22.0001" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 20C18.2091 20 20 18.2091 20 16C20 13.7909 18.2091 12 16 12C13.7909 12 12 13.7909 12 16C12 18.2091 13.7909 20 16 20Z" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M20.9955 21H21.0045" stroke="#23282fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

export const SHIELD_CROSS_DATA_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(SHIELD_CROSS_SVG)}`;
export const SHIELD_SEARCH_DATA_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(SHIELD_SEARCH_SVG)}`;

export const shouldReplaceThumbnail = (scanStatus: string | undefined): boolean => {
  return (
    scanStatus === ASSET_SCAN_STATUSES.QUARANTINED || scanStatus === ASSET_SCAN_STATUSES.PENDING
  );
};

export const getScanShieldUrl = (scanStatus: string | undefined): string | undefined => {
  if (scanStatus === ASSET_SCAN_STATUSES.QUARANTINED) return SHIELD_CROSS_DATA_URI;
  if (scanStatus === ASSET_SCAN_STATUSES.PENDING) return SHIELD_SEARCH_DATA_URI;
  return undefined;
};

export const getLeadingIcon = (scanStatus: string) => {
  if (scanStatus === ASSET_SCAN_STATUSES.CLEAN) {
    return (
      <Icon
        icon="CheckCircle"
        version="v2"
        width={20}
        height={20}
        className="info-icon"
        data-test-id="asset-scan-status-pill-icon-clean"
      />
    );
  }
  if (scanStatus === ASSET_SCAN_STATUSES.QUARANTINED) {
    return (
      <Icon
        icon="WarningBoldNew"
        version="v2"
        width={20}
        height={20}
        className="info-icon"
        data-test-id="asset-scan-status-pill-icon-quarantined"
      />
    );
  }
  if (scanStatus === ASSET_SCAN_STATUSES.PENDING) {
    return (
      <Icon
        icon="Clock"
        version="v2"
        width={20}
        height={20}
        className="info-icon"
        data-test-id="asset-scan-status-pill-icon-pending"
      />
    );
  }
  return undefined;
};

export const generateScanStatus = (assetScanStatus: string | undefined): { tooltip: string; icon: ReactNode } | undefined => {
  if (!assetScanStatus) return undefined;
  
  const tooltip = TOOLTIP_CONTENT[assetScanStatus] || '';
  const icon = getLeadingIcon(assetScanStatus);
  
  if (tooltip && icon) {
    return { tooltip, icon };
  }
  
  return undefined;
};